/**
 * Undo/redo: an operation log with inverses. The log is persisted alongside
 * the document (IndexedDB) so undo survives a browser refresh.
 *
 * The log is capped rather than truly unbounded. Because it is now
 * serialized on every autosave, a runaway log is not just a memory cost — it
 * is paid again on every write and on every restore. `MAX_OPS` is set far
 * above any realistic session (the previous cap was 2000, which was low
 * enough that a long drawing lost early undo steps) so undo stays
 * effectively unlimited in practice while keeping the worst case bounded.
 */

import { Element } from "./model";
import { World } from "./world";

export type Op =
  | { type: "add"; elements: Element[] }
  | { type: "remove"; elements: Element[] }
  | {
      type: "replace";
      before: Element[];
      after: Element[];
    }
  | {
      type: "modify";
      removed: Element[];
      added: Element[];
    };

/** Upper bound on retained undo entries; oldest are dropped past this. */
const MAX_OPS = 20000;

export interface HistoryData {
  version: 1;
  undo: Op[];
  redo: Op[];
}

export const HISTORY_VERSION = 1 as const;

export class History {
  private undoStack: Op[] = [];
  private redoStack: Op[] = [];
  private listeners = new Set<(h: History) => void>();

  constructor(private world: World) {}

  on(fn: (h: History) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoDepth(): number {
    return this.undoStack.length;
  }

  push(op: Op): void {
    this.undoStack.push(op);
    // Keep the newest MAX_OPS entries; oldest undo steps are dropped first.
    if (this.undoStack.length > MAX_OPS) {
      this.undoStack.splice(0, this.undoStack.length - MAX_OPS);
    }
    this.redoStack = [];
    this.emit();
  }

  undo(): boolean {
    const op = this.undoStack.pop();
    if (!op) return false;
    this.applyInverse(op);
    this.redoStack.push(op);
    this.emit();
    return true;
  }

  redo(): boolean {
    const op = this.redoStack.pop();
    if (!op) return false;
    this.apply(op);
    this.undoStack.push(op);
    this.emit();
    return true;
  }

  private apply(op: Op): void {
    switch (op.type) {
      case "add":
        this.world.addElements(op.elements);
        break;
      case "remove":
        for (const el of op.elements) this.world.removeElement(el.cellId, el.layerId, el.id);
        break;
      case "replace":
        for (const el of op.after) this.world.replaceElement(el);
        break;
      case "modify":
        for (const el of op.removed) this.world.removeElement(el.cellId, el.layerId, el.id);
        this.world.addElements(op.added);
        break;
    }
  }

  private applyInverse(op: Op): void {
    switch (op.type) {
      case "add":
        for (const el of op.elements) this.world.removeElement(el.cellId, el.layerId, el.id);
        break;
      case "remove":
        this.world.addElements(op.elements);
        break;
      case "replace":
        for (const el of op.before) this.world.replaceElement(el);
        break;
      case "modify":
        for (const el of op.added) this.world.removeElement(el.cellId, el.layerId, el.id);
        this.world.addElements(op.removed);
        break;
    }
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.emit();
  }

  /* ---------------------------- persistence ---------------------------- */

  /** Snapshot both stacks for persistence (structured-clone safe). */
  toData(): HistoryData {
    return {
      version: HISTORY_VERSION,
      undo: [...this.undoStack],
      redo: [...this.redoStack],
    };
  }

  /** Restore stacks previously captured with {@link toData}. */
  loadData(data: HistoryData | null | undefined): void {
    if (!data || typeof data !== "object") {
      this.clear();
      return;
    }
    const undo = Array.isArray((data as HistoryData).undo) ? (data as HistoryData).undo : [];
    const redo = Array.isArray((data as HistoryData).redo) ? (data as HistoryData).redo : [];
    this.undoStack = undo.filter(isOp);
    this.redoStack = redo.filter(isOp);
    this.emit();
  }

  serialize(): string {
    return JSON.stringify(this.toData());
  }

  load(json: string | null | undefined): void {
    if (!json) {
      this.clear();
      return;
    }
    try {
      const parsed = JSON.parse(json) as HistoryData;
      this.loadData(parsed);
    } catch {
      this.clear();
    }
  }
}

/**
 * Structural validation for ops restored from storage. History is read back
 * from IndexedDB, so it must be treated as untrusted input: a malformed op
 * would otherwise throw part-way through `undo()`/`redo()`, after the world had
 * already been mutated. Anything that does not match the expected shape is
 * dropped, which degrades to "less undo available" rather than a crash.
 */
function isOp(op: unknown): op is Op {
  if (typeof op !== "object" || op === null) return false;
  const o = op as Record<string, unknown>;
  const isElementArray = (v: unknown): boolean =>
    Array.isArray(v) && v.every((e) => typeof e === "object" && e !== null);
  switch (o.type) {
    case "add":
    case "remove":
      return isElementArray(o.elements);
    case "replace":
      return isElementArray(o.before) && isElementArray(o.after);
    case "modify":
      return isElementArray(o.removed) && isElementArray(o.added);
    default:
      return false;
  }
}
